import React, { useEffect, useState } from 'react'
import Select, { MultiValue } from 'react-select'
import styled from 'styled-components'
import { Flex, Input, Text } from '@workwhile/ui'
import { defs } from 'components/Shared/defs'
import { device } from 'components/Shared/device'
import { useIndeedFlexContextValue } from '../../context/IndeedFlexProvider'
import {
  INDEED_FLEX_SHIFT_ISSUE_OPTIONS,
  IndeedFlexShiftIssue,
} from '../../shared/constants/shiftIssues'

type NumberOption = { label: string; value: number }
type IssueOption = { label: string; value: IndeedFlexShiftIssue }

const findSelectedNumberOptions = (
  options: NumberOption[],
  values: number[] | null
) => (values ? options.filter((o) => values.includes(o.value)) : [])

const findSelectedIssueOptions = (
  options: IssueOption[],
  values: IndeedFlexShiftIssue[] | null
) => (values ? options.filter((o) => values.includes(o.value)) : [])

const toNumberValues = (selected: MultiValue<NumberOption>) =>
  selected.length ? selected.map((o) => o.value) : null

const toIssueValues = (selected: MultiValue<IssueOption>) =>
  selected.length ? selected.map((o) => o.value) : null

export const IndeedFlexIssuesFilterBar = () => {
  const {
    state: { clients, filters },
    actions: { setFilters },
  } = useIndeedFlexContextValue()

  const [daysAgoInput, setDaysAgoInput] = useState(String(filters.daysAgo))

  useEffect(() => {
    setDaysAgoInput(String(filters.daysAgo))
  }, [filters.daysAgo])

  const clientOptions: NumberOption[] = clients.map((client) => ({
    label: `${client.name} (${client.ifClientId})`,
    value: client.ifClientId,
  }))

  const companyOptions: NumberOption[] = clients
    .filter(
      (client) =>
        !filters.ifClientIds || filters.ifClientIds.includes(client.ifClientId)
    )
    .flatMap((client) => client.companies)
    .map((company) => ({
      label: `${company.name} (${company.id})`,
      value: company.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <FilterBarWrapper showBorderTop showBorderBottom>
      <SelectGroup>
        <Select
          isMulti
          isClearable
          isSearchable
          options={clientOptions}
          styles={customStyles}
          name="ifClientIds"
          placeholder="Client"
          value={findSelectedNumberOptions(clientOptions, filters.ifClientIds)}
          onChange={(selected) =>
            setFilters({ ifClientIds: toNumberValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isMulti
          isClearable
          isSearchable
          options={companyOptions}
          styles={customStyles}
          name="companyIds"
          placeholder="Company"
          value={findSelectedNumberOptions(companyOptions, filters.companyIds)}
          onChange={(selected) =>
            setFilters({ companyIds: toNumberValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isMulti
          isClearable
          isSearchable
          options={INDEED_FLEX_SHIFT_ISSUE_OPTIONS}
          styles={issueSelectStyles}
          name="issues"
          placeholder="Issue"
          value={findSelectedIssueOptions(
            INDEED_FLEX_SHIFT_ISSUE_OPTIONS,
            filters.issues
          )}
          onChange={(selected) =>
            setFilters({ issues: toIssueValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Flex flexDirection="row" alignItems="center">
          <Input
            type="number"
            min={1}
            step={1}
            value={daysAgoInput}
            onChange={(event) => {
              setDaysAgoInput(event.target.value)
              const parsed = Number.parseInt(event.target.value, 10)
              if (Number.isNaN(parsed)) return
              setFilters({ daysAgo: Math.max(1, parsed) })
            }}
            style={{ width: 50 }}
          />
          <Text marginLeft={1}>days ago</Text>
        </Flex>
      </SelectGroup>
    </FilterBarWrapper>
  )
}

const customStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 220,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const issueSelectStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 260,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const FilterBarWrapper = styled.div<{
  showBorderTop?: boolean
  showBorderBottom?: boolean
}>`
  border-bottom: ${(props) => (props.showBorderBottom ? '1px solid' : null)};
  border-top: ${(props) => (props.showBorderTop ? '1px solid' : null)};
  border-color: ${defs.veryLightBlack};
  padding: ${defs.paddingS};
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fafafa;
  @media ${device.smallerThanMobileL} {
    font-size: ${defs.fontS};
    height: 100%;
    padding-left: ${defs.paddingM};
  }
`

const SelectGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`
